<script setup lang="ts">
  import {
    computed,
    getCurrentInstance,
    onMounted,
    shallowRef,
    watch,
  } from 'vue';
  import { useRouter } from 'vue-router/composables';
  import type { IRestaurantSettings } from '@/api/services/restaurants.service';
  import V2MenuContextBridge from '@/components/v2/menu-import/V2MenuContextBridge.vue';
  import V2MenuFlow from '@/components/v2/menu-import/V2MenuFlow.vue';
  import V2MenuTopActions from '@/components/v2/menu-import/workspace/V2MenuTopActions.vue';
  import V2SetupBottomBar from '@/components/v2/store-setup/shell/V2SetupBottomBar.vue';
  import V2SetupShell from '@/components/v2/store-setup/shell/V2SetupShell.vue';
  import V2SetupTopBar from '@/components/v2/store-setup/shell/V2SetupTopBar.vue';
  import type { IMenuFlowHandle } from '@/composition/menu-import/menu-import-context';
  import { EMPTY_FLOW_BAR } from '@/composition/store-setup/setup-flow-screen';
  import type { IFlowBarState } from '@/composition/store-setup/setup-flow-screen';
  import { useMenuUpdateUndo } from '@/composition/menu-import/use-menu-update-undo';
  import { useMenuV2 } from '@/composition/use-menu-v2';
  import { useSetupLeaveGuard } from '@/composition/store-setup/use-setup-leave-guard';
  import { useStableWindowSize } from '@/composition/use-stable-window-size';
  import { useUi } from '@/composition/use-ui';
  import { publishedToast } from '@/domain/menu-import/publish-dialog';
  import { menuLaterTarget } from '@/domain/menu-import/workspace-view';
  import { AlertType } from '@/interfaces/IAlert';
  import type { IPublishResponse } from '@/interfaces/IMenuImport';
  import type { IRestaurant } from '@/interfaces/IRestaurant';
  import { ignoreNavigationFailure } from '@/utils/navigation.utils';

  /**
   * `/:restaurantId/menu/import` (plan §4.11): the same menu source screen
   * and workspace for stores that already exist, without the stepper or the
   * skip. Stores with several menus are out of scope for the AI import: they
   * go back to their items page.
   */
  const WIDE_MIN_WIDTH = 1024;
  const PHONE_MAX_WIDTH = 768;
  /** «Listo, su menú está actualizado.» keeps its «Deshacer» a while. */
  const UNDO_TOAST_MS = 10000;

  const props = withDefaults(
    defineProps<{
      restaurant: IRestaurant;
      restaurantSettings?: IRestaurantSettings | null;
    }>(),
    { restaurantSettings: null }
  );
  const proxy = getCurrentInstance()!.proxy;
  const router = useRouter();
  const ui = useUi();
  const menuUpdateUndo = useMenuUpdateUndo();
  const { width } = useStableWindowSize();

  /** The menu flow (its exposed handle; shallow: its context holds refs). */
  const activeScreen = shallowRef<IMenuFlowHandle | null>(null);

  const wide = computed(() => width.value >= WIDE_MIN_WIDTH);
  const phone = computed(() => width.value < PHONE_MAX_WIDTH);
  const multipleMenus = computed(
    () => props.restaurantSettings?.multipleMenus === true
  );
  const screenKey = computed(
    () => `menu:${activeScreen.value?.innerKey ?? ''}`
  );
  const bar = computed<IFlowBarState>(() =>
    activeScreen.value ? activeScreen.value.bar : EMPTY_FLOW_BAR
  );
  /** «Actualizar menú» for a store that already has dishes; nothing until that is known. */
  const pageTitle = computed(() => {
    const context = activeScreen.value?.context;
    if (!context || context.loadState.value !== 'ready') return null;
    return context.existingItems.value > 0
      ? 'menuImport.update.pageTitle'
      : 'menuImport.pageTitle';
  });

  useSetupLeaveGuard(() => activeScreen.value?.busy === true);

  function itemsPage() {
    return `/${props.restaurant.stringId}/items`;
  }

  function leaveMultipleMenus() {
    void router.replace(itemsPage()).catch(ignoreNavigationFailure);
  }

  function exit() {
    void router
      .push(`/${props.restaurant.stringId}/dashboard`)
      .catch(ignoreNavigationFailure);
  }

  const { isMenuV2Active } = useMenuV2();

  /** «Seguir después» from the top bar's ⋯ menu. */
  function later() {
    void router
      .push(
        menuLaterTarget(
          'import',
          props.restaurant.stringId,
          isMenuV2Active.value
        )
      )
      .catch(ignoreNavigationFailure);
  }

  /** Published here: what changed (an update keeps «Deshacer» a while), then the items page. */
  function onPublished(response: IPublishResponse, sessionId: string) {
    const toast = publishedToast(response);
    ui.showToast(
      AlertType.Success,
      String(proxy.$t(toast.key)),
      toast.undoable
        ? {
            text: String(proxy.$t('menuImport.undo')),
            cb: () => void menuUpdateUndo.undo(sessionId),
          }
        : undefined,
      toast.undoable ? UNDO_TOAST_MS : undefined
    );
    void router.push(itemsPage()).catch(ignoreNavigationFailure);
  }

  /** Published by another device or tab: the items page. */
  function onPublishedElsewhere() {
    ui.showToast(
      AlertType.Success,
      String(proxy.$t('menuImport.publishedToast'))
    );
    void router.push(itemsPage()).catch(ignoreNavigationFailure);
  }

  //#region Bottom bar
  function onPrimary() {
    void activeScreen.value?.primary();
  }

  function onSecondary() {
    void activeScreen.value?.secondary();
  }

  function onBack() {
    void activeScreen.value?.back();
  }
  //#endregion

  // Another store in the same page may have several menus.
  watch(multipleMenus, (on) => {
    if (on) leaveMultipleMenus();
  });

  onMounted(() => {
    if (multipleMenus.value) leaveMultipleMenus();
  });
</script>

<script lang="ts">
  import type { NavigationGuardNext, Route } from 'vue-router';
  import { confirmSetupLeave } from '@/composition/store-setup/use-setup-leave-guard';

  // vue-router 3 has no onBeforeRouteLeave composable: the in-component guard
  // is an option, and reaches the page's state through the leave guard's
  // module-scoped hook (registered by useSetupLeaveGuard above).
  export default {
    async beforeRouteLeave(to: Route, from: Route, next: NavigationGuardNext) {
      next((await confirmSetupLeave()) ? undefined : false);
    },
  };
</script>

<template>
  <V2SetupShell
    class="v2-menu-import-page"
    :wide="wide"
    :screen-key="screenKey"
  >
    <template #top>
      <V2SetupTopBar :title="pageTitle ? $t(pageTitle) : ''" @exit="exit">
        <template #actions>
          <V2MenuContextBridge
            v-if="activeScreen"
            :key="activeScreen.contextId"
            :context="activeScreen.context"
          >
            <V2MenuTopActions :wide="wide" @later="later" />
          </V2MenuContextBridge>
        </template>
      </V2SetupTopBar>
    </template>
    <V2MenuFlow
      v-if="!multipleMenus"
      ref="activeScreen"
      entry="import"
      :restaurant="restaurant"
      :wide="wide"
      :phone="phone"
      @published="onPublished"
      @published-elsewhere="onPublishedElsewhere"
    />
    <template #bottom>
      <V2SetupBottomBar
        :state="bar"
        :wide="wide"
        @back="onBack"
        @left="onBack"
        @secondary="onSecondary"
        @primary="onPrimary"
      />
    </template>
  </V2SetupShell>
</template>
